import type { GeoJSONMultiPolygon, RowGeoResponse } from "api/model";

import {
  fromBoundaryMultiPolygon,
  toBoundaryMultiPolygon,
} from "./boundaryUtils";
import { distanceSq, pointAlongSegment, type BoundaryPoint } from "./geo";
import {
  boundaryRing,
  edgeKey,
  findSnappedVertexId,
  getVertexPoint,
  rowLongSideCenterline,
  toDegrees,
  toRadians,
  NEAR_ZERO_LENGTH_EPSILON,
} from "./gridMeshGeometry";
import type {
  GridCell,
  GridEdgeRef,
  GridMesh,
  GridRow,
  GridVertex,
} from "./gridMeshTypes";

/** Id for a row-divider vertex, unique within its plot: `gv-row-{plotGeoId}-{n}`. */
export const rowDividerVertexId = (plotGeoId: number, index: number): string =>
  `gv-row-${plotGeoId}-${index}`;

/** Row record needed to split a plot and write its boundary back. */
export interface PlotRowSeed {
  id: number;
  plot_id: number;
  row_number: number;
}

/**
 * Interior vertices from `start` toward `end` at t = 1/n … (n-1)/n.
 * `n` is the row count, so this returns n-1 points.
 */
const interiorPoints = (
  start: BoundaryPoint,
  end: BoundaryPoint,
  rowCount: number,
  vertexIdAt: (index: number) => string,
): { ids: string[]; vertices: GridVertex[] } => {
  const ids: string[] = [];
  const vertices: GridVertex[] = [];
  for (let index = 1; index < rowCount; index += 1) {
    const id = vertexIdAt(index);
    const point = pointAlongSegment(start, end, index / rowCount);
    ids.push(id);
    vertices.push({
      id,
      latitude: point.latitude,
      longitude: point.longitude,
    });
  }
  return { ids, vertices };
};

/** Rows for this plot inside `[minRow, maxRow]`, ordered by `row_number`. */
const rowsForPlot = (
  rows: readonly PlotRowSeed[],
  cell: GridCell,
): PlotRowSeed[] =>
  rows
    .filter((row) => {
      if (row.plot_id !== cell.plotGeoId) {
        return false;
      }
      if (cell.minRow == null || cell.maxRow == null) {
        return true;
      }
      return row.row_number >= cell.minRow && row.row_number <= cell.maxRow;
    })
    .sort((left, right) => left.row_number - right.row_number);

const splitQuad = (
  cell: GridCell,
  vertices: Record<string, GridVertex>,
  plotRows: PlotRowSeed[],
): { cell: GridCell; rows: GridRow[]; vertices: GridVertex[] } | null => {
  const plotGeoId = cell.plotGeoId;
  if (
    plotGeoId == null ||
    cell.vertexIds.length !== 4 ||
    plotRows.length === 0
  ) {
    return null;
  }

  const [aId, bId, cId, dId] = cell.vertexIds;
  if (aId == null || bId == null || cId == null || dId == null) {
    return null;
  }
  const a = getVertexPoint(vertices, aId);
  const b = getVertexPoint(vertices, bId);
  const c = getVertexPoint(vertices, cId);
  const d = getVertexPoint(vertices, dId);
  if (a == null || b == null || c == null || d == null) {
    return null;
  }

  // Vertex 0 → 1 is the crop-row axis (min_row toward max_row on a committed
  // lattice cell). Strips are ordered along that edge.
  const rowCount = plotRows.length;

  if (rowCount === 1) {
    const only = plotRows[0];
    if (only == null) {
      return null;
    }
    return {
      cell,
      rows: [
        {
          id: `gr-${only.id}`,
          plotCellId: cell.id,
          plotGeoId,
          rowGeoId: only.id,
          rowNumber: only.row_number,
          vertexIds: [...cell.vertexIds],
        },
      ],
      vertices: [],
    };
  }

  const dividerCount = rowCount - 1;
  const near = interiorPoints(a, b, rowCount, (index) =>
    rowDividerVertexId(plotGeoId, index),
  );
  const far = interiorPoints(d, c, rowCount, (index) =>
    rowDividerVertexId(plotGeoId, dividerCount + index),
  );
  near.vertices.forEach((vertex, index) => {
    vertex.onEdge = {
      endVertexId: bId,
      startVertexId: aId,
      t: (index + 1) / rowCount,
    };
  });
  far.vertices.forEach((vertex, index) => {
    vertex.onEdge = {
      endVertexId: cId,
      startVertexId: dId,
      t: (index + 1) / rowCount,
    };
  });
  const added = [...near.vertices, ...far.vertices];

  const gridRows: GridRow[] = plotRows.map((row, index) => {
    const startBoundary = index === 0 ? aId : near.ids[index - 1];
    const endBoundary = index === rowCount - 1 ? bId : near.ids[index];
    const startFar = index === 0 ? dId : far.ids[index - 1];
    const endFar = index === rowCount - 1 ? cId : far.ids[index];

    const vertexIds = [startBoundary, endBoundary, endFar, startFar];

    return {
      id: `gr-${row.id}`,
      plotCellId: cell.id,
      plotGeoId,
      rowGeoId: row.id,
      rowNumber: row.row_number,
      vertexIds: vertexIds.filter(
        (vertexId): vertexId is string => vertexId != null,
      ),
    };
  });

  return {
    cell,
    rows: gridRows,
    vertices: added,
  };
};

/**
 * Split each quad plot cell into one strip per row in `[minRow, maxRow]`.
 * Strips run from vertex 0 toward vertex 1, which is min_row toward max_row
 * on a cell committed from the lattice.
 */
export const splitMeshPlotsIntoRows = (
  mesh: GridMesh,
  rows: readonly PlotRowSeed[],
): GridMesh => {
  if (rows.length === 0) {
    return mesh;
  }

  const vertices: Record<string, GridVertex> = { ...mesh.vertices };
  const gridRows: GridRow[] = [];
  const cells = mesh.cells.map((cell) => {
    if (cell.plotGeoId == null) {
      return cell;
    }
    const plotRows = rowsForPlot(rows, cell);
    const split = splitQuad(cell, vertices, plotRows);
    if (split == null) {
      return cell;
    }
    split.vertices.forEach((vertex) => {
      vertices[vertex.id] = vertex;
    });
    gridRows.push(...split.rows);
    return split.cell;
  });

  if (gridRows.length === 0) {
    return { cells, vertices };
  }

  return { cells, rows: gridRows, vertices };
};

/** Plot-edge hit strictly between the endpoints. Endpoints snap to vertices. */
const findPlotEdgeHit = (
  cell: GridCell,
  vertices: Record<string, GridVertex>,
  point: BoundaryPoint,
): { endVertexId: string; startVertexId: string; t: number } | null => {
  const ids = cell.vertexIds;
  const count = ids.length;
  for (let index = 0; index < count; index += 1) {
    const startVertexId = ids[index];
    const endVertexId = ids[(index + 1) % count];
    if (startVertexId == null || endVertexId == null) {
      continue;
    }
    const start = getVertexPoint(vertices, startVertexId);
    const end = getVertexPoint(vertices, endVertexId);
    if (start == null || end == null) {
      continue;
    }
    const lengthSq = distanceSq(start, end);
    if (lengthSq <= NEAR_ZERO_LENGTH_EPSILON) {
      continue;
    }
    const t =
      ((point.longitude - start.longitude) * (end.longitude - start.longitude) +
        (point.latitude - start.latitude) * (end.latitude - start.latitude)) /
      lengthSq;
    if (t <= 0 || t >= 1) {
      continue;
    }
    const projected = pointAlongSegment(start, end, t);
    if (distanceSq(point, projected) > NEAR_ZERO_LENGTH_EPSILON) {
      continue;
    }
    return { endVertexId, startVertexId, t };
  }

  return null;
};

/** Plot-ring vertices plus this plot's row vertices. Other plots' dividers are excluded. */
const verticesForPlotSnap = (
  cell: GridCell,
  vertices: Record<string, GridVertex>,
  plotRows: readonly GridRow[],
): Record<string, GridVertex> => {
  const snap: Record<string, GridVertex> = {};
  const include = (vertexId: string) => {
    const vertex = vertices[vertexId];
    if (vertex != null) {
      snap[vertex.id] = vertex;
    }
  };

  cell.vertexIds.forEach(include);
  plotRows.forEach((plotRow) => {
    plotRow.vertexIds.forEach(include);
  });
  return snap;
};

const buildSavedRow = (
  row: RowGeoResponse,
  cell: GridCell,
  vertices: Record<string, GridVertex>,
  snapVertices: Record<string, GridVertex>,
  nextDividerVertexId: (plotGeoId: number) => string,
): { row: GridRow; vertices: GridVertex[] } | null => {
  const plotGeoId = cell.plotGeoId;
  if (plotGeoId == null || row.boundary == null) {
    return null;
  }

  const openRing = fromBoundaryMultiPolygon(row.boundary);
  if (openRing == null) {
    return null;
  }

  const snapTargets: Record<string, GridVertex> = { ...snapVertices };
  const added: GridVertex[] = [];
  const vertexIds: string[] = [];

  openRing.forEach((point) => {
    const snappedId = findSnappedVertexId(snapTargets, point);
    if (snappedId != null) {
      if (vertexIds[vertexIds.length - 1] !== snappedId) {
        vertexIds.push(snappedId);
      }
      return;
    }

    const edge = findPlotEdgeHit(cell, vertices, point);
    const id = nextDividerVertexId(plotGeoId);
    const edgeStart =
      edge == null ? null : getVertexPoint(vertices, edge.startVertexId);
    const edgeEnd =
      edge == null ? null : getVertexPoint(vertices, edge.endVertexId);
    const position =
      edge == null || edgeStart == null || edgeEnd == null
        ? point
        : pointAlongSegment(edgeStart, edgeEnd, edge.t);
    const vertex: GridVertex = {
      id,
      latitude: position.latitude,
      longitude: position.longitude,
      ...(edge == null
        ? {}
        : {
            onEdge: {
              endVertexId: edge.endVertexId,
              startVertexId: edge.startVertexId,
              t: edge.t,
            },
          }),
    };
    snapTargets[id] = vertex;
    added.push(vertex);
    if (vertexIds[vertexIds.length - 1] !== id) {
      vertexIds.push(id);
    }
  });

  if (
    vertexIds.length > 1 &&
    vertexIds[0] === vertexIds[vertexIds.length - 1]
  ) {
    vertexIds.pop();
  }
  if (vertexIds.length < 3) {
    return null;
  }

  return {
    row: {
      id: `gr-${row.id}`,
      plotCellId: cell.id,
      plotGeoId,
      rowGeoId: row.id,
      rowNumber: row.row_number,
      vertexIds,
    },
    vertices: added,
  };
};

/**
 * Rebuild row strips from saved row polygons on an existing plot mesh.
 * Corners snap to plot vertices. Divider endpoints that lie on a plot edge
 * become `onEdge` vertices so later drags stay on that boundary. A divider
 * only snaps to vertices of its own plot, so it can move independently of
 * the divider in the next plot.
 */
export const attachSavedRowBoundaries = (
  mesh: GridMesh,
  rows: readonly RowGeoResponse[],
): GridMesh => {
  const cellByPlotId = new Map<number, GridCell>();
  mesh.cells.forEach((cell) => {
    if (cell.plotGeoId != null) {
      cellByPlotId.set(cell.plotGeoId, cell);
    }
  });

  const matched = rows
    .filter((row) => row.boundary != null && cellByPlotId.has(row.plot_id))
    .sort((left, right) =>
      left.plot_id === right.plot_id
        ? left.row_number - right.row_number
        : left.plot_id - right.plot_id,
    );
  if (matched.length === 0) {
    return mesh;
  }

  const vertices: Record<string, GridVertex> = { ...mesh.vertices };
  const gridRows: GridRow[] = [];
  const nextDividerIndexByPlot = new Map<number, number>();
  const nextDividerVertexId = (plotGeoId: number): string => {
    const index = nextDividerIndexByPlot.get(plotGeoId) ?? 1;
    nextDividerIndexByPlot.set(plotGeoId, index + 1);
    return rowDividerVertexId(plotGeoId, index);
  };
  matched.forEach((row) => {
    const cell = cellByPlotId.get(row.plot_id);
    if (cell == null) {
      return;
    }
    const built = buildSavedRow(
      row,
      cell,
      vertices,
      verticesForPlotSnap(
        cell,
        vertices,
        gridRows.filter((gridRow) => gridRow.plotGeoId === cell.plotGeoId),
      ),
      nextDividerVertexId,
    );
    if (built == null) {
      return;
    }
    built.vertices.forEach((vertex) => {
      vertices[vertex.id] = vertex;
    });
    gridRows.push(built.row);
  });

  if (gridRows.length === 0) {
    return mesh;
  }

  return { cells: mesh.cells, rows: gridRows, vertices };
};

const sharesPlotEdge = (
  left: NonNullable<GridVertex["onEdge"]>,
  right: NonNullable<GridVertex["onEdge"]>,
): boolean =>
  left.startVertexId === right.startVertexId &&
  left.endVertexId === right.endVertexId;

/** Edges that cross a plot between opposite sides, splitting one row from the next. */
export const collectRowDividerEdges = (mesh: GridMesh): GridEdgeRef[] => {
  const unique = new Map<string, GridEdgeRef>();
  (mesh.rows ?? []).forEach((row) => {
    const count = row.vertexIds.length;
    for (let index = 0; index < count; index += 1) {
      const startVertexId = row.vertexIds[index];
      const endVertexId = row.vertexIds[(index + 1) % count];
      if (startVertexId == null || endVertexId == null) {
        continue;
      }
      const startEdge = mesh.vertices[startVertexId]?.onEdge;
      const endEdge = mesh.vertices[endVertexId]?.onEdge;
      if (
        startEdge == null ||
        endEdge == null ||
        sharesPlotEdge(startEdge, endEdge)
      ) {
        continue;
      }
      const key = edgeKey(startVertexId, endVertexId);
      if (unique.has(key)) {
        continue;
      }
      unique.set(key, { endVertexId, startVertexId });
    }
  });

  return [...unique.values()];
};

/**
 * Divider lines for the saved-row layer. Stroking each row ring would draw
 * the shared divider twice, once from each ring. Those dash patterns are out
 * of phase, so the gaps look smaller than the single divider drawn in edit mode.
 */
export const toRowDividerGeoJson = (mesh: GridMesh) => {
  const features = collectRowDividerEdges(mesh).flatMap((edge) => {
    const start = mesh.vertices[edge.startVertexId];
    const end = mesh.vertices[edge.endVertexId];
    if (start == null || end == null) {
      return [];
    }

    return [
      {
        type: "Feature" as const,
        geometry: {
          type: "LineString" as const,
          coordinates: [
            [start.longitude, start.latitude] as [number, number],
            [end.longitude, end.latitude] as [number, number],
          ],
        },
        properties: {
          kind: "row-divider" as const,
        },
      },
    ];
  });

  return {
    type: "FeatureCollection" as const,
    features,
  };
};

type RowLabelKind = "grid-row-label" | "row-label";

/**
 * Clockwise degrees from horizontal so text reads along `start` → `end`.
 * The centerline already points east, or north when the side is vertical.
 */
const rotationAlong = (
  start: { latitude: number; longitude: number },
  end: { latitude: number; longitude: number },
): number => {
  const lngScale =
    Math.cos(toRadians((start.latitude + end.latitude) / 2)) || 1;
  const east = (end.longitude - start.longitude) * lngScale;
  const north = end.latitude - start.latitude;
  return toDegrees(Math.atan2(east, north)) - 90;
};

/** One `range/row` label at the strip centroid, rotated along its long side. */
export const toRowLabelFeature = <Kind extends RowLabelKind>(
  ring: readonly BoundaryPoint[],
  range: number,
  rowNumber: number,
  kind: Kind,
) => {
  const centerline = rowLongSideCenterline(ring);
  if (centerline == null) {
    return null;
  }

  const [start, end] = centerline;
  return {
    type: "Feature" as const,
    geometry: {
      type: "Point" as const,
      coordinates: [
        (start.longitude + end.longitude) / 2,
        (start.latitude + end.latitude) / 2,
      ] as [number, number],
    },
    properties: {
      kind,
      label: `${range}/${rowNumber}`,
      rotation: rotationAlong(start, end),
    },
  };
};

/** One `range/row` label per row, rotated along the long side. */
export const collectRowLabelFeatures = <Kind extends RowLabelKind>(
  mesh: GridMesh,
  kind: Kind,
) => {
  const rangeByCellId = new Map(
    mesh.cells.flatMap((cell) =>
      cell.range == null ? [] : [[cell.id, cell.range] as const],
    ),
  );

  return (mesh.rows ?? []).flatMap((row) => {
    const range = rangeByCellId.get(row.plotCellId);
    if (range == null) {
      return [];
    }

    const feature = toRowLabelFeature(
      boundaryRing(row.vertexIds, mesh.vertices),
      range,
      row.rowNumber,
      kind,
    );
    return feature == null ? [] : [feature];
  });
};

/** `range/row` labels for the saved-row layer. */
export const toRowLabelGeoJson = (mesh: GridMesh) => ({
  type: "FeatureCollection" as const,
  features: collectRowLabelFeatures(mesh, "row-label"),
});

/** Row boundary payloads for PATCH /rows-geo/bulk. */
export const meshToRowGeoBulkItems = (
  mesh: GridMesh,
): Array<{ boundary: GeoJSONMultiPolygon; id: number }> => {
  const items: Array<{ boundary: GeoJSONMultiPolygon; id: number }> = [];
  (mesh.rows ?? []).forEach((row) => {
    const boundary = toBoundaryMultiPolygon(
      boundaryRing(row.vertexIds, mesh.vertices),
    );
    if (boundary == null) {
      return;
    }
    items.push({ boundary, id: row.rowGeoId });
  });
  return items;
};
